'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertTriangle, Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { config } from '@/lib/config'

// ============================================================================
// Types & Schema
// ============================================================================

const whopFormSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required'),
  productId: z.string().min(1, 'Product ID is required'),
  webhookSecret: z.string().min(1, 'Webhook Secret is required'),
  disableNativeCreditCardForm: z.boolean(),
})

type FormValues = z.infer<typeof whopFormSchema>

interface WhopFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
  storeId?: string
}

// ============================================================================
// Component
// ============================================================================

export function WhopForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
  storeId,
}: WhopFormProps) {
  const { t } = useTranslation('integrations')
  const [copiedWebhook, setCopiedWebhook] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(whopFormSchema),
    defaultValues: {
      apiKey: defaultValues.credentials?.apiKey ?? '',
      productId: defaultValues.credentials?.productId ?? '',
      webhookSecret: defaultValues.credentials?.webhookSecret ?? '',
      disableNativeCreditCardForm: defaultValues.credentials?.disableNativeCreditCardForm ?? false,
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const credentials = data
    await onSubmit({ credentials })
  }

  // Must resolve to the API host. The CRM host serves the SPA and answers every /api/* path
  // with 200 + HTML, which Whop records as a successful delivery and never retries.
  const webhookUrl = `${config.apiBaseUrl}/api/public/webhooks/whop`

  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopiedWebhook(true)
      setTimeout(() => setCopiedWebhook(false), 2000)
    } catch (error) {
      console.error('Failed to copy webhook URL:', error)
    }
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Warning Alert */}
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{t('whop.limitationsTitle')}</AlertTitle>
        <AlertDescription>
          <p className="mb-2 text-sm">{t('whop.limitationsDescription')}</p>
          <ul className="list-inside list-disc space-y-1 text-sm">
            <li>{t('whop.limitation1')}</li>
            <li>{t('whop.limitation2')}</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('whop.apiCredentials')}</CardTitle>
          <CardDescription>{t('whop.apiCredentialsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">
              {t('whop.apiKey')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={t('whop.apiKeyPlaceholder')}
              disabled={isLoading}
              {...register('apiKey')}
            />
            <p className="text-xs text-muted-foreground">
              {t('whop.apiKeyHint')}
            </p>
            {errors.apiKey && (
              <p className="text-xs text-destructive">{errors.apiKey.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="productId">
              {t('whop.productId')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="productId"
              placeholder={t('whop.productIdPlaceholder')}
              disabled={isLoading}
              {...register('productId')}
            />
            <p className="text-xs text-muted-foreground">
              {t('whop.productIdHint')}
            </p>
            {errors.productId && (
              <p className="text-xs text-destructive">{errors.productId.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhookSecret">
              {t('whop.webhookSecret')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="webhookSecret"
              type="password"
              placeholder={t('whop.webhookSecretPlaceholder')}
              disabled={isLoading}
              {...register('webhookSecret')}
            />
            <p className="text-xs text-muted-foreground">
              {t('whop.webhookSecretHint')}
            </p>
            {errors.webhookSecret && (
              <p className="text-xs text-destructive">{errors.webhookSecret.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Webhook URL */}
      <Card>
        <CardHeader>
          <CardTitle>{t('whop.webhookUrl')}</CardTitle>
          <CardDescription>{t('whop.webhookUrlDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm">
              {webhookUrl}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyWebhook}
              disabled={isLoading}
            >
              {copiedWebhook ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('whop.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>{t('whop.setupStep1')}</li>
              <li>{t('whop.setupStep2')}</li>
              <li>{t('whop.setupStep3')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('whop.setupStep4') }} />
              <li>{t('whop.setupStep5')}</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
